import React, { useState, useEffect } from "react";
import { FileUp } from "lucide-react";
import httpClient from "../../../../../../api/httpClient";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

// Falls back to TL's document-type set when movementType is missing/unrecognized,
// since TL is the default/most common mode and every mode's set is a superset of
// the 3 baseline document types today.
const DEFAULT_DOC_TYPE_MODE_CODE = "tl_shipment_doc_type";

// FTL and TL share one code_mapping row (see V211__seed_document_type_code_mapping_by_mode.sql)
// since they're the same document-type set today.
const modeToDocTypeCode = (movementType) => {
  const normalized = (movementType || "").toUpperCase();
  const mode = normalized === "FTL" ? "TL" : normalized;
  return mode ? `${mode.toLowerCase()}_shipment_doc_type` : DEFAULT_DOC_TYPE_MODE_CODE;
};

const formatDocTypeLabel = (code) =>
  code
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const DocumentUploadModal = ({
  onClose,
  shipmentNumber,
  onUploadSuccess,
  shipmentOfficeCode,
  movementType,
}) => {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("BILL_OF_LADING");
  const [docTypeOptions, setDocTypeOptions] = useState([
    "BILL_OF_LADING",
    "INVOICE",
    "PROOF_OF_DELIVERY",
  ]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchDocTypeOptions = async () => {
      try {
        const modeCode = modeToDocTypeCode(movementType);
        const response = await httpClient.get(
          `/api/code-mapping/DOCUMENT_TYPE/${modeCode}`
        );
        const [mapping] = Array.isArray(response.data) ? response.data : [];
        const codes = (mapping?.description || "")
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);

        if (codes.length > 0) {
          setDocTypeOptions(codes);
          setFileType((current) => (codes.includes(current) ? current : codes[0]));
        }
      } catch (error) {
        console.error("Error fetching document type options:", error);
        // Keep the default 3-option fallback already in state.
      }
    };

    fetchDocTypeOptions();
  }, [movementType]);

  const handleUpload = async () => {
    if (!file) {
      toast.warn("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const token = Cookies.get("authToken");

    const uploadUrl = `/api/upload/shipmentDocuments/${shipmentNumber}?fileType=${fileType}`;

    try {
      setIsUploading(true);

      const response = await httpClient.post(uploadUrl, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Office-Code": shipmentOfficeCode,
          "Content-Type": "multipart/form-data"
        }
      });

      const match = /File (.+?) added/.exec(response.data);
      if (match) {
        const newDoc = match[1];
        onUploadSuccess?.(newDoc, fileType);
      }

      toast.success("Document uploaded successfully!");
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.content || "Upload failed. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40 px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-fluid-4 space-y-fluid-4"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* ========================== HEADER ========================== */}
        <div className="flex flex-col items-center space-y-fluid-1.5">
          <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white shadow-md">
            <FileUp size={32} />
          </div>

          <h2 className="text-fluid-xl font-semibold text-[#111827]">
            Upload Document
          </h2>

          <p className="text-fluid-sm text-gray-600">
            Shipment #:{" "}
            <span className="font-medium text-[#1e2a74]">
              {shipmentNumber}
            </span>
          </p>
        </div>

        {/* ======================= SECTION BOX ======================= */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-fluid-3 space-y-fluid-3">

          {/* File Type */}
          <div className="space-y-fluid-1">
            <label className="text-fluid-sm font-medium text-gray-800">
              File Type
            </label>
            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-fluid-2 py-fluid-2 text-fluid-sm"
            >
              {docTypeOptions.map((code) => (
                <option key={code} value={code}>
                  {formatDocTypeLabel(code)}
                </option>
              ))}
            </select>
          </div>

          {/* File Input */}
          <div className="space-y-fluid-1">
            <label className="text-fluid-sm font-medium text-gray-800">
              Select File
            </label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.svg"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full border border-gray-300 rounded-lg px-fluid-2 py-fluid-2 text-fluid-sm cursor-pointer bg-white focus:ring-2 focus:ring-[#1e2a74]"
            />
          </div>
        </div>

        {/* ======================= FOOTER BUTTONS ======================= */}
        <div className="flex justify-end gap-fluid-2 pt-fluid-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
            disabled={isUploading}
          >
            Cancel
          </button>

          <button
            onClick={handleUpload}
            disabled={isUploading}
            className={`px-fluid-button-x py-fluid-button-y text-white text-fluid-sm rounded font-semibold transition ${
              isUploading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#1e2a74] hover:bg-[#18225c]"
            }`}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DocumentUploadModal;
